import React, { useRef, useState } from 'react';
import { Menu, MenuItem, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SelfieCaptureDialog from './SelfieCaptureDialog';
import { MAX_SECONDARY_REFERENCE_PHOTOS } from '../../config/appConfig';
import './EventGallery.css';

// A row of small round reference-photo avatars, used everywhere a request or
// a reference set's secondary (non-primary) photos are shown: the guest
// submission form, the attendee's own request edit view, the organiser's
// request detail panel, and the organiser's reference-set editor. Hovering
// an avatar reveals a delete overlay; a trailing round "+" tile (hidden once
// `maxCount` is reached) opens a small menu to capture or upload another.
//
// `photos` is [{photo_key, url}]. Callers own the actual upload/delete API
// calls - onAdd(file) and onRemove(photoKey) are handed the raw action and
// may be async; this component just tracks which single item is mid-flight
// so it can show a spinner instead of double-submitting.
export default function ReferencePhotoRow({
  photos = [],
  onAdd,
  onRemove,
  maxCount = MAX_SECONDARY_REFERENCE_PHOTOS,
  readOnly = false,
  label = 'Additional reference photos (optional)',
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [camOpen, setCamOpen] = useState(false);
  const [pendingKey, setPendingKey] = useState('');
  const fileInputRef = useRef(null);

  const atLimit = photos.length >= maxCount;

  async function handleFile(file) {
    if (!file || !onAdd) return;
    setPendingKey('__adding__');
    try {
      await onAdd(file);
    } finally {
      setPendingKey('');
    }
  }

  async function handleRemove(photoKey) {
    if (!onRemove) return;
    setPendingKey(photoKey);
    try {
      await onRemove(photoKey);
    } finally {
      setPendingKey('');
    }
  }

  return (
    <div className="ref-photo-row-wrap">
      {label && <span className="ref-photo-row-label">{label}</span>}
      <div className="ref-photo-row">
        {photos.map(photo => (
          <div className="ref-photo-avatar" key={photo.photo_key}>
            <img src={photo.url} alt="Reference" />
            {!readOnly && (
              <button
                type="button"
                className="ref-photo-avatar-delete-overlay"
                onClick={() => handleRemove(photo.photo_key)}
                disabled={Boolean(pendingKey)}
                aria-label="Remove reference photo"
              >
                {pendingKey === photo.photo_key ? <CircularProgress size={16} color="inherit" /> : <CloseIcon fontSize="small" />}
              </button>
            )}
          </div>
        ))}
        {!readOnly && !atLimit && (
          <>
            <button
              type="button"
              className="ref-photo-add-tile"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              disabled={Boolean(pendingKey)}
              aria-label="Add reference photo"
            >
              {pendingKey === '__adding__' ? <CircularProgress size={18} /> : <AddIcon />}
            </button>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
              <MenuItem onClick={() => { setMenuAnchor(null); setCamOpen(true); }}>
                <CameraAltIcon fontSize="small" sx={{ mr: 1 }} /> Take a selfie
              </MenuItem>
              <MenuItem onClick={() => { setMenuAnchor(null); fileInputRef.current?.click(); }}>
                <FileUploadOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Choose from gallery
              </MenuItem>
            </Menu>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
            />
          </>
        )}
      </div>
      {!readOnly && (
        <span className="ref-photo-row-hint">
          {atLimit ? `Maximum ${maxCount} reference photos reached.` : `${photos.length} of ${maxCount} added`}
        </span>
      )}
      <SelfieCaptureDialog
        open={camOpen}
        onClose={() => setCamOpen(false)}
        onCapture={(file) => { setCamOpen(false); handleFile(file); }}
      />
    </div>
  );
}
